import { ENV } from "@/env";
import { DEFAULT_LOCALE, parseSupportedLocales, type SupportedLocale } from "@/shared/culture/locales";
import { mergeTranslations, type TranslationDictionary } from "@/shared/culture/mergeTranslations";
import { resolveLocale } from "@/shared/culture/resolveLocale";
import { loadModuleTranslations, loadTranslations } from "@/shared/culture/translationLoader";
import type { TranslationModule } from "./translationModules.generated";
import { appI18n, appI18nInitialization } from "./i18n";

export type InitialCulture = {
  locale: SupportedLocale;
  translations: TranslationDictionary;
  preloadedModules: TranslationModule[];
};

function getInitialModulesForPath(pathname: string): TranslationModule[] {
  const pathToModule: Array<{ prefix: string; module: TranslationModule }> = [
    { prefix: "/admin", module: "admin" },
    { prefix: "/captain", module: "captain" },
    { prefix: "/dashboards", module: "dashboards" },
    { prefix: "/identity-and-access", module: "identity-and-access" },
    { prefix: "/resources", module: "resources" },
    { prefix: "/sources", module: "sources" },
    { prefix: "/storage-hub", module: "storage" },
    { prefix: "/uns", module: "uns" },
  ];

  const matchingModules = pathToModule.filter(({ prefix }) => pathname === prefix || pathname.startsWith(`${prefix}/`)).map(({ module }) => module);

  return Array.from(new Set(matchingModules));
}

export async function preloadInitialCulture(): Promise<InitialCulture> {
  await appI18nInitialization;

  const allowedLocales = parseSupportedLocales(ENV.SUPPORTED_LOCALES);
  const locale = resolveLocale(allowedLocales);
  const preloadedModules = getInitialModulesForPath(window.location.pathname);

  try {
    let translations = await loadTranslations(locale);

    if (preloadedModules.length > 0) {
      const moduleTranslations = await Promise.all(preloadedModules.map((moduleName) => loadModuleTranslations(locale, moduleName)));
      for (const moduleTranslation of moduleTranslations) {
        translations = mergeTranslations(translations, moduleTranslation);
      }
    }

    appI18n.addResourceBundle(locale, "translation", translations[locale] ?? {}, true, true);
    await appI18n.changeLanguage(locale);

    return { locale, translations, preloadedModules };
  } catch (error) {
    console.error(`Error preloading translations for locale ${locale}:`, error);
    await appI18n.changeLanguage(locale);

    return { locale: locale || DEFAULT_LOCALE, translations: {}, preloadedModules: [] };
  }
}
